/* Dripurr — Exit-intent welcome popup
 * Self-contained: injects its own CSS + markup, triggers on exit intent
 * (desktop) / scroll (mobile), and subscribes to the Klaviyo Email List
 * client-side so the welcome flow fires. Captures a zero-party `cat_goal`.
 *
 * Load site-wide by adding this before </body> in the theme layout:
 *   <script src="{{ 'dripurr-popup.js' | asset_url }}" defer></script>
 */
(() => {
  "use strict";

  // ---------- Config ----------
  const KLAVIYO_COMPANY = "RiGVQ2";      // public company id (site key)
  const KLAVIYO_LIST    = "TwSm7P";      // "Email List" — triggers the welcome flow
  const WELCOME_CODE    = "WELCOME10";   // static 10%-off code (shown on-screen + emailed)
  const LOGO  = "https://cdn.shopify.com/s/files/1/0979/2179/4416/files/dripurr-cat-water-fountain-logo.png";
  const PHOTO = "https://cdn.shopify.com/s/files/1/0979/2179/4416/files/dripurr-cat-water-fountain-kitchen-lifestyle.png";

  const SEEN_KEY = "dripurr_popup_seen";   // ts of last show
  const DONE_KEY = "dripurr_popup_done";   // subscribed — never show again
  const COOLDOWN_DAYS = 14;

  const QUESTIONS = [
    { label: "My cat barely drinks water",          value: "hydration" },
    { label: "My cat won't use their bowl",         value: "picky" },
    { label: "I want fresher, cleaner water",       value: "water_quality" },
    { label: "My vet said they need to drink more", value: "vet_directed" }
  ];

  // ---------- Guards ----------
  const path = location.pathname;
  if (/\/(cart|checkout|checkouts|account)(\/|$)/.test(path)) return;
  if (localStorage.getItem(DONE_KEY)) return;
  const seen = Number(localStorage.getItem(SEEN_KEY) || 0);
  if (seen && Date.now() - seen < COOLDOWN_DAYS * 864e5) return;

  let goal = null;
  let opened = false;
  let submitted = false;

  // ---------- Styles ----------
  const css = `
  .dpx-ov{position:fixed;inset:0;z-index:2147483000;background:rgba(11,30,48,.55);
    display:flex;align-items:flex-start;justify-content:center;overflow-y:auto;
    -webkit-overflow-scrolling:touch;overscroll-behavior:contain;padding:20px;
    opacity:0;visibility:hidden;transition:opacity .25s ease;
    font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;-webkit-font-smoothing:antialiased;}
  .dpx-ov.dpx-show{opacity:1;visibility:visible;}
  .dpx-card{position:relative;width:100%;max-width:660px;margin:auto;background:#fff;border-radius:18px;
    overflow:hidden;display:flex;box-shadow:0 24px 60px rgba(0,0,0,.35);
    transform:translateY(10px) scale(.98);transition:transform .25s ease;}
  .dpx-ov.dpx-show .dpx-card{transform:none;}
  .dpx-close{position:absolute;top:12px;right:12px;width:34px;height:34px;border:0;cursor:pointer;
    border-radius:50%;background:rgba(255,255,255,.92);color:#0F3D6E;font-size:19px;line-height:1;z-index:5;
    box-shadow:0 2px 8px rgba(0,0,0,.18);}
  .dpx-media{flex:0 0 44%;position:relative;min-height:410px;background:#D7EAF8;}
  .dpx-media img{position:absolute;inset:0;width:100%;height:100%;object-fit:cover;display:block;}
  .dpx-flag{position:absolute;top:18px;left:16px;background:#F0682F;color:#fff;font-size:12px;
    font-weight:700;letter-spacing:.03em;padding:7px 13px;border-radius:999px;
    transform:rotate(-7deg);box-shadow:0 3px 8px rgba(0,0,0,.18);}
  .dpx-body{flex:1 1 56%;padding:30px 30px 26px;}
  .dpx-logo{height:32px;display:block;margin-bottom:16px;}
  .dpx-kicker{font-size:16px;color:#8A5A44;font-weight:500;}
  .dpx-big{font-size:56px;line-height:.95;font-weight:800;color:#0F3D6E;letter-spacing:-.02em;}
  .dpx-sub{font-size:20px;font-weight:600;color:#E0714D;margin-top:2px;}
  .dpx-q{font-size:14px;color:#5F5E5A;margin:16px 0 12px;}
  .dpx-pills{display:flex;flex-direction:column;gap:9px;}
  .dpx-pill{width:100%;text-align:center;border:1.5px solid #F0CDB9;background:#FCEBE0;color:#7A2E12;
    font-weight:500;font-size:15px;padding:13px 16px;border-radius:999px;cursor:pointer;
    font-family:inherit;transition:background .12s,border-color .12s,color .12s;}
  .dpx-pill:hover{background:#F6D4C1;border-color:#E9B096;}
  .dpx-pill.dpx-on{background:#0F3D6E;border-color:#0F3D6E;color:#fff;}
  .dpx-form{margin-top:22px;padding-top:20px;border-top:1px solid #EFE9E2;}
  .dpx-email{width:100%;box-sizing:border-box;border:1.5px solid #D9DEE3;border-radius:10px;
    padding:13px 14px;font-size:15px;font-family:inherit;color:#1b2733;}
  .dpx-email:focus{outline:none;border-color:#1E6FB8;}
  .dpx-name{margin-bottom:9px;}
  .dpx-cta{width:100%;margin-top:9px;border:0;cursor:pointer;background:#0F3D6E;color:#fff;
    font-weight:700;font-size:16px;padding:15px;border-radius:10px;font-family:inherit;
    transition:background .12s;}
  .dpx-cta:hover{background:#0b3057;}
  .dpx-cta:disabled{opacity:.65;cursor:default;}
  .dpx-err{color:#B2352B;font-size:13px;margin-top:8px;min-height:16px;}
  .dpx-decline{display:block;width:100%;text-align:center;margin-top:14px;background:none;border:0;
    cursor:pointer;font-size:13px;color:#9A9A96;text-decoration:underline;font-family:inherit;}
  .dpx-ok{text-align:center;padding:26px 6px;}
  .dpx-ok h3{font-size:26px;color:#0F3D6E;font-weight:800;margin:14px 0 8px;}
  .dpx-ok p{font-size:15px;color:#5F5E5A;line-height:1.5;margin:0;}
  .dpx-code{display:flex;align-items:stretch;gap:0;max-width:320px;margin:18px auto 10px;
    border:2px dashed #F0682F;border-radius:12px;overflow:hidden;background:#FCEBE0;}
  .dpx-code code{flex:1 1 auto;display:flex;align-items:center;justify-content:center;
    font-family:inherit;font-weight:800;font-size:22px;letter-spacing:.14em;color:#7A2E12;
    padding:14px 10px 14px 16px;}
  .dpx-copy{flex:0 0 auto;border:0;cursor:pointer;background:#0F3D6E;color:#fff;
    font-family:inherit;font-weight:700;font-size:14px;padding:0 18px;
    transition:background .12s;}
  .dpx-copy:hover{background:#0b3057;}
  .dpx-copy.dpx-copied{background:#1F8A54;}
  @media (max-width:720px){
    .dpx-ov{padding:12px;align-items:flex-start;}
    .dpx-card{flex-direction:column;max-width:400px;}
    .dpx-media{flex:none;min-height:120px;height:120px;}
    .dpx-body{padding:20px 20px 18px;}
    .dpx-big{font-size:42px;}
    .dpx-sub{font-size:18px;}
    .dpx-logo{height:26px;margin-bottom:10px;}
    .dpx-q{margin:12px 0 10px;}
    .dpx-pills{gap:8px;}
    .dpx-pill{padding:12px 14px;font-size:14.5px;}
    .dpx-form{margin-top:16px;padding-top:16px;}
  }`;

  // ---------- Markup ----------
  const ov = document.createElement("div");
  ov.className = "dpx-ov";
  ov.setAttribute("role", "dialog");
  ov.setAttribute("aria-modal", "true");
  ov.setAttribute("aria-label", "10% off your first Dripurr order");
  ov.innerHTML = `
    <div class="dpx-card">
      <button class="dpx-close" aria-label="Close">&times;</button>
      <div class="dpx-media">
        <img src="${PHOTO}" alt="Cat drinking from a Dripurr fountain">
        <span class="dpx-flag">New filters</span>
      </div>
      <div class="dpx-body" data-stage="form">
        <img class="dpx-logo" src="${LOGO}" alt="Dripurr">
        <div class="dpx-kicker">An Additional</div>
        <div class="dpx-big">10% off</div>
        <div class="dpx-sub">for you!</div>
        <div class="dpx-q">To claim, tell us what's going on with your cat:</div>
        <div class="dpx-pills">
          ${QUESTIONS.map(q => `<button type="button" class="dpx-pill" data-v="${q.value}">${q.label}</button>`).join("")}
        </div>
        <form class="dpx-form" novalidate>
          <input class="dpx-email dpx-name" type="text" name="cat_name" placeholder="Your cat's name (optional)" autocomplete="off" maxlength="40">
          <input class="dpx-email" type="email" name="email" placeholder="Your email address" autocomplete="email" required>
          <button class="dpx-cta" type="submit">Claim my 10% off</button>
          <div class="dpx-err" aria-live="polite"></div>
        </form>
        <button class="dpx-decline" type="button">No thanks, my cat can stay thirsty</button>
      </div>
    </div>`;

  const style = document.createElement("style");
  style.textContent = css;

  function mount() {
    document.head.appendChild(style);
    document.body.appendChild(ov);

    ov.querySelector(".dpx-close").addEventListener("click", () => close());
    ov.querySelector(".dpx-decline").addEventListener("click", () => close());
    ov.addEventListener("click", e => { if (e.target === ov) close(); });

    ov.querySelectorAll(".dpx-pill").forEach(btn => {
      btn.addEventListener("click", () => {
        ov.querySelectorAll(".dpx-pill").forEach(p => p.classList.remove("dpx-on"));
        btn.classList.add("dpx-on");
        goal = btn.getAttribute("data-v");
      });
    });

    ov.querySelector(".dpx-form").addEventListener("submit", onSubmit);
  }

  // ---------- Open / close ----------
  function open() {
    if (opened) return;
    opened = true;
    localStorage.setItem(SEEN_KEY, String(Date.now()));
    ov.classList.add("dpx-show");
    document.documentElement.style.overflow = "hidden";
    const first = ov.querySelector('input[name="cat_name"]') || ov.querySelector('input[name="email"]');
    if (first) setTimeout(() => first.focus({ preventScroll: true }), 300);
  }

  function close() {
    ov.classList.remove("dpx-show");
    document.documentElement.style.overflow = "";
    teardownTriggers();
  }

  // ---------- Submit ----------
  function onSubmit(e) {
    e.preventDefault();
    if (submitted) return;
    const input = ov.querySelector('input[name="email"]');
    const err = ov.querySelector(".dpx-err");
    const cta = ov.querySelector(".dpx-cta");
    const email = (input.value || "").trim();
    const nameEl = ov.querySelector('input[name="cat_name"]');
    const catName = nameEl ? (nameEl.value || "").trim().slice(0, 40) : "";
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      err.textContent = "Please enter a valid email address.";
      input.focus();
      return;
    }
    err.textContent = "";
    submitted = true;
    cta.disabled = true;
    cta.textContent = "Claiming…";

    subscribe(email, goal, catName)
      .then(() => {
        localStorage.setItem(DONE_KEY, "1");
        renderSuccess(catName);
      })
      .catch(() => {
        // Still mark done locally; welcome flow can be retried, don't nag on error.
        submitted = false;
        cta.disabled = false;
        cta.textContent = "Claim my 10% off";
        err.textContent = "Something went wrong — please try again.";
      });
  }

  function subscribe(email, catGoal, catName) {
    // Subscribing to the single-opt-in Email List via the client endpoint IS the
    // consent — the client endpoint rejects a `subscriptions` field on the profile.
    const props = {};
    if (catGoal) props.cat_goal = catGoal;
    if (catName) props.cat_name = catName;

    const profileAttrs = { email: email };
    if (Object.keys(props).length) profileAttrs.properties = props;

    const body = {
      data: {
        type: "subscription",
        attributes: {
          custom_source: "Exit-intent popup" + (catGoal ? " · " + catGoal : ""),
          profile: { data: { type: "profile", attributes: profileAttrs } }
        },
        relationships: { list: { data: { type: "list", id: KLAVIYO_LIST } } }
      }
    };

    // Best-effort: also identify on the onsite profile so the properties stick.
    try {
      window._learnq = window._learnq || [];
      const idPayload = { $email: email };
      if (catGoal) idPayload.cat_goal = catGoal;
      if (catName) idPayload.cat_name = catName;
      window._learnq.push(["identify", idPayload]);
    } catch (_) {}

    return fetch("https://a.klaviyo.com/client/subscriptions/?company_id=" + KLAVIYO_COMPANY, {
      method: "POST",
      headers: { "Content-Type": "application/json", "revision": "2024-10-15" },
      body: JSON.stringify(body)
    }).then(r => {
      if (!r.ok) throw new Error("klaviyo " + r.status);
      return r;
    });
  }

  function renderSuccess(catName) {
    const el = ov.querySelector(".dpx-body");
    const esc = s => String(s).replace(/[&<>"']/g, c =>
      ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
    const heading = catName ? `You're in — hi, ${esc(catName)}! 🐾` : "You're in! 🐾";
    el.innerHTML = `
      <div class="dpx-ok">
        <img class="dpx-logo" src="${LOGO}" alt="Dripurr" style="margin:0 auto 14px;">
        <h3>${heading}</h3>
        <p>Here's your 10% off — use it at checkout:</p>
        <div class="dpx-code">
          <code>${esc(WELCOME_CODE)}</code>
          <button class="dpx-copy" type="button" aria-label="Copy code">Copy</button>
        </div>
        <p>We've also sent it to your inbox so it's there when you're ready.</p>
      </div>`;

    const btn = el.querySelector(".dpx-copy");
    if (btn) {
      btn.addEventListener("click", () => {
        const done = () => {
          btn.textContent = "Copied!";
          btn.classList.add("dpx-copied");
          setTimeout(() => { btn.textContent = "Copy"; btn.classList.remove("dpx-copied"); }, 2000);
        };
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(WELCOME_CODE).then(done, done);
        } else {
          const t = document.createElement("textarea");
          t.value = WELCOME_CODE; t.style.position = "fixed"; t.style.opacity = "0";
          document.body.appendChild(t); t.select();
          try { document.execCommand("copy"); } catch (_) {}
          document.body.removeChild(t); done();
        }
      });
    }
    // Give people time to copy the code before auto-dismissing.
    setTimeout(close, 15000);
  }

  // ---------- Triggers ----------
  function onMouseOut(e) {
    if (e.clientY <= 0 && !e.relatedTarget) { open(); }
  }
  function onScroll() {
    const h = document.documentElement;
    const pct = (h.scrollTop || document.body.scrollTop) / ((h.scrollHeight - h.clientHeight) || 1);
    if (pct >= 0.5) open();
  }
  let safety;
  function setupTriggers() {
    if (window.matchMedia("(min-width:721px)").matches) {
      document.addEventListener("mouseout", onMouseOut);
    } else {
      window.addEventListener("scroll", onScroll, { passive: true });
    }
    safety = setTimeout(open, 25000); // last-resort so mobile always sees it
  }
  function teardownTriggers() {
    document.removeEventListener("mouseout", onMouseOut);
    window.removeEventListener("scroll", onScroll);
    clearTimeout(safety);
  }

  // ---------- Boot ----------
  function boot() { mount(); setupTriggers(); }
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
})();
